/* eslint-disable jsx-a11y/label-has-for */
// @flow

import { rem } from 'polished';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { currency } from 'client/utils/format';
import Checkmark from 'client/components/checkmark';
import { DividerNoMargin } from 'client/components/divider';
import { getNextMonth, hasDeadlineDisplayLogic } from 'client/utils/enrollmentPeriods';
import { mergeAppJson } from 'client/utils/questions';
import BREAKPOINTS from 'client/constants/styles/breakpoints';
import CartPlanDetailContainer from 'client/components/cart/CartPlanDetailContainer';
import COLORS from 'client/constants/colors';
import CORE_PRODUCT_TYPES from 'client/constants/productTypes';
import ContentfulTranslate from 'client/components/ContentfulTranslate';
import EnrollmentPeriodModel from 'routes/health/models/EnrollmentPeriodModel';
import ErrorNotificationScroll from 'client/components/form/errorNotificationScroll';
import FunnelButton from 'client/components/button/funnelButton';
import FunnelHeader from 'client/components/funnelHeader';
import ROUTES from 'client/constants/routes';
import usePrevious from 'client/hooks/usePrevious';

import { trackCartView } from 'routes/cart/util/tracking';
import {
  PriceContainer,
  RichTextPreWrapTranslate,
  SectionContent,
  SectionHeader,
} from 'routes/cart/components/shared';
import CartPlanItem from 'routes/cart/components/CartPlanItem';
import CartCrossSellSectionContainer from 'routes/cart/components/crossSell/CartCrossSellSectionContainer';
import DeadlineWarningContainer from 'routes/cart/components/deadline/DeadlineWarningContainer';

import PlanUtils from 'web-core/src/utils/plan';
import 'routes/health/styles/ichra.scss';

import { type TransformedRecommendationDataType } from 'types/health/plan.flow';
import type { EnrollmentPeriodResType } from 'web-core/types/enrollment';
import type { IfpAppType } from 'web-core/types/ifp-app';
import { trackIchraConsentBoxChecked } from 'routes/health/utils/trackings';

const NoCrossSellContainer = styled.div`
  background-color: ${COLORS.ORANGE_4};
  margin-left: auto;
  margin-right: auto;
  margin-top: 1rem;
  padding: 1rem;
  text-align: center;
  width: ${rem(500)};

  @media screen and (min-width: ${BREAKPOINTS.MEDIUM}) {
    border: 1px solid ${COLORS.ORANGE_2};
  }

  @media screen and (max-width: ${BREAKPOINTS.MEDIUM}) {
    border-bottom: 1px solid ${COLORS.ORANGE_2};
    margin-top: 0;
    text-align: left;
    width: 100%;
  }
`;

const DisclaimerTranslate = styled(RichTextPreWrapTranslate)`
  p {
    font-size: ${rem(14)};
    line-height: 1.42857;
  }
`;

const DisclaimerHeaderTranslate = styled(DisclaimerTranslate)`
  b {
    color: ${COLORS.ORANGE_2};
  }
`;

const SubHeaderContainer = styled.div`
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5rem;
`;

const MedicaidWarningContainer = styled.div`
  color: ${COLORS.GRAY_2};
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  padding-top: 2rem;
`;

type Props = {
  cartApps: Array<IfpAppType>,
  checkoutRoute: string,
  enrollmentPeriods: EnrollmentPeriodResType,
  hasDentalOrVisionInCart: boolean,
  hasFFMApp: boolean,
  healthApp: ?IfpAppType,
  isHealthCareGov: boolean,
  plans: Array<TransformedRecommendationDataType> | null,
  postPlan: Function,
  putApp: Function,
  shouldDisplayCrossSell: boolean,
  shouldDisplayMedicaidWarning: boolean,
  phase3Enabled: boolean,
  partnerDisplayName: string,
  ichraContribution: number,
  isEnrolled: boolean,
  partnerName: string,
};

const CartPage = (props: Props) => {
  const {
    cartApps,
    checkoutRoute,
    enrollmentPeriods,
    hasDentalOrVisionInCart,
    hasFFMApp,
    healthApp,
    isHealthCareGov,
    plans,
    postPlan,
    putApp,
    shouldDisplayCrossSell,
    shouldDisplayMedicaidWarning,
    phase3Enabled,
    partnerDisplayName,
    partnerName,
    ichraContribution,
    isEnrolled,
  } = props;

  const [ichraConsent, setIchraConsent] = useState(true);
  const premiumTotal = cartApps.reduce((acc, { premium }) => +acc + +premium, 0).toFixed(2);
  const [enrollmentPeriodInfo, setEnrollmentPeriodInfo] = useState({});
  const [displayDeadlineWarning, setDisplayDeadlineWarning] = useState(false);
  const [hasError, setHasError] = useState(false);
  const previousHealthRecs = usePrevious(plans);

  useEffect(() => {
    trackCartView(cartApps);
  }, [cartApps]);

  useEffect(() => {
    if (enrollmentPeriods.current && enrollmentPeriods.next) {
      setEnrollmentPeriodInfo(new EnrollmentPeriodModel(enrollmentPeriods.current, enrollmentPeriods.next));
      setDisplayDeadlineWarning(hasDeadlineDisplayLogic(enrollmentPeriods));
    }
  }, [enrollmentPeriods]);

  useEffect(() => {
    if (healthApp && !_.isEqual(plans, previousHealthRecs)) {
      const syncHealthPlan = async () => {
        const { app_id, app_json } = healthApp;
        const currentPlan = _.get(healthApp, 'app_json.plan', {});
        const planRecommendations = _.map(plans, rec => rec.plan);
        let match = _.find(planRecommendations, { id: currentPlan.id });

        /*
         * if the plan version isn't there but the master version is
         * save that version to the app
         */
        if (!match && currentPlan.masterId) {
          match = _.find(planRecommendations, {
            masterId: currentPlan.masterId,
          });
          if (match) {
            const planData = match;
            mergeAppJson(app_json || {}, 'plan', planData);
            await putApp(app_id, { app_json });
            await postPlan(app_id, PlanUtils.formatPlanArguments(planData));
          }
        }
      };
      syncHealthPlan();
    }
  }, [plans]);

  if (_.isEmpty(cartApps) && typeof window !== 'undefined') {
    window.location.href = phase3Enabled ? ROUTES.PHASE3_DASHBOARD_ROUTE : ROUTES.PHASE2_DASHBOARD_ROUTE;
    return null;
  }

  const NoCrossSellDisclaimer = () => (
    <NoCrossSellContainer>
      <DisclaimerHeaderTranslate path="cart.noCrossSellDisclaimerHeader" />
      <DisclaimerTranslate path="cart.noCrossSellDisclaimerContent" />
    </NoCrossSellContainer>
  );

  const CartPageHeader = () => (
    <React.Fragment>
      <FunnelHeader>
        <ContentfulTranslate path="cart.header" />
      </FunnelHeader>
      <div className="text-center pad-bottom-2">
        <SubHeaderContainer>
          <ContentfulTranslate
            path={`cart.${hasFFMApp && isHealthCareGov ? 'allThatsLeftToDoValueProp' : 'allThatsLeftToDo'}`}
            using={{ partnerName: partnerDisplayName }} />
          <SubHeaderContainer>
            <ContentfulTranslate path={`cart.${healthApp ? 'completionEstimate' : 'completionEstimateDv'}`} />
          </SubHeaderContainer>
        </SubHeaderContainer>
      </div>
    </React.Fragment>
  );

  const CartSummary = () => (
    <div className="pad-x-small-only">
      <div className="flex-row space-between">
        <SectionHeader>
          <ContentfulTranslate path="cart.orderSummary" />
        </SectionHeader>
        <SectionContent>
          <ContentfulTranslate path="cart.monthlyPrice" />
        </SectionContent>
      </div>
      <DividerNoMargin />
      {cartApps.map(app => (
        <CartPlanDetailContainer key={app.app_id} app={app}>
          {cartPlanDetailProps => (
            <CartPlanItem
              {...cartPlanDetailProps}
              allowRemovePlan={cartApps.length > 1}
              // Use enrollment period effective date if health is added in cart, otherwise default to next month
              effectiveDate={healthApp ? enrollmentPeriodInfo.effectiveDate : getNextMonth()}
              excludePlan={cartPlanDetailProps.type !== CORE_PRODUCT_TYPES.HEALTH && hasFFMApp}
              onError={() => setHasError(true)}
              onRemovePlan={() => setHasError(false)} />
          )}
        </CartPlanDetailContainer>
      ))}
      <div className="flex-row space-between">
        <SectionHeader>
          <ContentfulTranslate path="cart.total" />
        </SectionHeader>
        <PriceContainer isTotal>{premiumTotal}</PriceContainer>
      </div>
      {shouldDisplayMedicaidWarning && (
        <MedicaidWarningContainer>
          <ContentfulTranslate path="cart.medicaidWarning" />
        </MedicaidWarningContainer>
      )}
    </div>
  );

  const getCtaPath = () => {
    let path = cartApps.length > 1 ? 'applyPlansCta' : 'applyPlanCta';
    if (enrollmentPeriodInfo.isPeriodEnd) {
      path = 'applyFinalDeadlineCta';
    } else if (enrollmentPeriodInfo.showCutoffMissed) {
      path = 'applyOnStrideCta';
    }
    return path;
  };

  const CartCta = () => (
    <FunnelButton
      clickHandler={() => {
        if (ichraContribution && ichraConsent) {
          trackIchraConsentBoxChecked(partnerName);
        }
        window.location.href = checkoutRoute;
        return null;
      }}
      isEde={false}
      isSubmitting={false}>
      <ContentfulTranslate path={`cart.${getCtaPath()}`} using={{ partnerName: partnerDisplayName }} />
    </FunnelButton>
  );

  const Ichra = () => {
    if (!ichraContribution) return null;
    const premium = healthApp.premium * 1;
    const premiumAfterIchra = premium > ichraContribution ? premium - ichraContribution : 0;
    return (
      <div className="center-col-500 pad-x-small-only">
        <div className="ichra-after-reimbursment-estimate">
          <ContentfulTranslate
            path="cart.premiumAfterIchra"
            using={{ amount: currency(premiumAfterIchra) }} />
        </div>
        <div className="checkbox-container ichra-checkbox">
          <Checkmark
            id="ichra"
            style={{ width: '2rem', height: '2rem' }}
            checked={ichraConsent}
            clickHandler={() => {
              setIchraConsent(!ichraConsent);
            }} />
          <label htmlFor="ichra">
            <ContentfulTranslate path="cart.ichraConsent" using={{ partnerName }} />
          </label>
        </div>
        {!ichraConsent && (
          <div className="ichra-warning">
            <ContentfulTranslate path="cart.ichraConsentWarning" using={{ partnerName }} />
          </div>
        )}
      </div>
    );
  };

  const displayNoCrossSellDisclaimer = hasFFMApp && !isEnrolled && hasDentalOrVisionInCart;

  return (
    <React.Fragment>
      {hasError && <ErrorNotificationScroll translateKey="errors.cart" shouldShow={hasError} />}
      {displayNoCrossSellDisclaimer && <NoCrossSellDisclaimer />}
      <div className="center-col-500 pad-x-small-only">
        <CartPageHeader />
        <CartSummary />
      </div>
      <Ichra />
      {displayDeadlineWarning && <DeadlineWarningContainer enrollmentPeriodInfo={enrollmentPeriodInfo} />}
      <CartCta />
      {shouldDisplayCrossSell && <CartCrossSellSectionContainer />}
    </React.Fragment>
  );
};

export default CartPage;
